import { useState } from "react";
import confetti from "canvas-confetti";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Sparkles, MoveHorizontal, Gift } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

export const BeforeAfterGame = () => {
  const [position, setPosition] = useState(10);
  const [isWon, setIsWon] = useState(false);

  const handleDrag = (val: number) => {
    if (isWon) return;
    setPosition(val);
    
    if (val >= 99) {
      setIsWon(true);
      setPosition(100);
      confetti({
        particleCount: 200,
        spread: 80,
        origin: { y: 0.6 },
        colors: ['#0ea5e9', '#38bdf8', '#ffffff', '#0284c7']
      });
    }
  };

  return (
    <section className="py-24 bg-primary/5 relative overflow-hidden">
      <div className="container px-4 md:px-6 max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground font-bold text-sm mb-4">
            <Sparkles className="w-4 h-4" />
            Mini-Game
          </div>
          <h2 className="text-3xl md:text-5xl font-bold mb-4 text-foreground">Swipe the Dirt Away!</h2>
          <p className="text-xl text-foreground/90 max-w-2xl mx-auto">
            Drag the slider all the way to the right to reveal the spotless room and unlock a special surprise.
          </p>
        </div>

        <div className="grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <Card className="relative w-full aspect-video rounded-2xl overflow-hidden border-4 border-white shadow-2xl">
              {/* Dirty Image (Background) */}
              <img 
                src="https://vibe.filesafe.space/1776798830316075366/assets/95a07abb-acad-4810-8b98-e72287a0617a.png" 
                alt="Dirty Room"
                className="absolute inset-0 w-full h-full object-cover"
              />

              {/* Clean Image (Foreground, clipped) */}
              <div 
                className="absolute inset-0 w-full h-full"
                style={{ clipPath: `polygon(0 0, ${position}% 0, ${position}% 100%, 0 100%)` }}
              >
                <img 
                  src="https://vibe.filesafe.space/1776798830316075366/assets/6c8dbe48-e329-42b9-a572-3b1f8b577353.png" 
                  alt="Clean Room"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>

              {/* Slider Handle */}
              <div 
                className="absolute top-0 bottom-0 w-2 bg-primary cursor-ew-resize shadow-[0_0_15px_rgba(0,0,0,0.5)] z-10"
                style={{ left: `${position}%` }}
              >
                <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-14 h-14 bg-white dark:bg-card rounded-full flex items-center justify-center shadow-xl border-4 border-primary text-primary text-2xl hover:scale-110 transition-transform">
                  🧽
                </div>
              </div>

              {/* Hidden Range Input for Accessibility & Touch/Mouse Handling */}
              <input 
                type="range" 
                min="0" 
                max="100" 
                value={position} 
                onChange={(e) => handleDrag(Number(e.target.value))}
                className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize z-20"
                aria-label="Drag to clean the room"
                disabled={isWon}
              />
            </Card>
          </div>

          <div className="md:col-span-4">
            <Card className={cn(
              "p-8 text-center transition-all duration-500 border-2",
              isWon ? "bg-primary text-primary-foreground border-primary shadow-xl scale-105" : "bg-card text-card-foreground border-dashed border-muted-foreground/30"
            )}>
              {isWon ? (
                <div className="space-y-6 anim-fade-in is-visible">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Gift className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold">You Did It!</h3>
                  <p className="text-primary-foreground">
                    Enjoy a spotless home and <strong>10% OFF</strong> your first cleaning with us.
                  </p>
                  <div className="bg-background/20 py-3 px-4 rounded-lg border border-white/30 backdrop-blur-sm">
                    <p className="text-sm font-medium mb-1 text-primary-foreground">Use Promo Code:</p>
                    <p className="text-3xl font-mono font-bold tracking-widest text-primary-foreground">UPLEVEL10</p>
                  </div>
                  <Button asChild size="lg" variant="secondary" className="w-full font-bold text-primary">
                    <Link to="/book" aria-label="Claim discount and book now">Claim Discount Now</Link>
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <Sparkles className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground">Reward Locked</h3>
                  <p className="text-sm font-medium text-foreground/90">
                    Drag the slider all the way to the right to clean the room and reveal your exclusive discount code!
                  </p>
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
